import { https } from "../http";
import { showError } from "../utils/index";
import { uploadFileToS3Api } from "../utils/PresignedUrlFunction";
import swal from "sweetalert";

const PROVIDER_DOCUMENTS_PREFIX = "provider_documents";
export const ALLOWED_PROVIDER_DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"];

const getExtension = (filename = "") => filename.split(".").pop()?.toLowerCase();

export const validateProviderDocument = (file) => {
  const extension = getExtension(file?.name);
  if (!ALLOWED_PROVIDER_DOCUMENT_EXTENSIONS.includes(extension)) {
    return `Unsupported file type ".${extension || ""}". Allowed: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG.`;
  }
  return null;
};

// Without a folder this returns the provider root — every document, filed or
// not — mirroring the "All Documents" listing used for staff/client records.
// With a folder it returns that folder's documents plus its subfolders.
const getProviderDocuments = async (providerId, folderId) => {
  try {
    let url = `/skypointProvider/${providerId}/documents`;
    if (folderId) url += `?folder=${folderId}`;

    const response = await https().get(url);
    if (response?.status === 200) {
      return {
        documents: response?.data?.documents || [],
        folderInfo: response?.data?.folderInfo || null,
      };
    }
    return { documents: [], folderInfo: null };
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return { documents: [], folderInfo: null };
  }
};

const getProviderFolders = async (providerId, search) => {
  try {
    let url = `/skypointProvider/${providerId}/folders`;
    if (search) url += `?search=${encodeURIComponent(search)}`;

    const response = await https().get(url);
    if (response?.status === 200) {
      return response?.data?.folders || [];
    }
    return [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

// `folderId` nests the new folder inside an existing one; omit it to create at
// the provider root.
const createProviderFolder = async (providerId, name, folderId) => {
  try {
    const response = await https().post(`/skypointProvider/${providerId}/folders`, {
      name,
      ...(folderId && { folderId }),
    });
    return response?.status === 200;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const renameProviderFolder = async (providerId, folderId, name) => {
  try {
    const response = await https().put(`/skypointProvider/${providerId}/folders/${folderId}`, { name });
    return response?.status === 200;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const deleteProviderFolder = async (providerId, folderId) => {
  try {
    const response = await https().delete(`/skypointProvider/${providerId}/folders/${folderId}`);
    if (response?.status === 200) {
      swal({ title: "Folder deleted", icon: "success", dangerMode: false });
      return true;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// Uploads straight to S3 via the existing presigned pipeline (under the
// provider_documents/ folder, not the shared profile_photos/ one), then records
// the metadata against the provider. If the metadata POST fails, nothing is
// left behind in the list — the S3 object is simply orphaned, never a row.
const uploadProviderDocument = async (providerId, file, onProgress, folderId, expiry) => {
  const validationError = validateProviderDocument(file);
  if (validationError) {
    showError(validationError);
    return false;
  }

  try {
    onProgress?.(true);
    const { uploadURL } = await uploadFileToS3Api({
      file,
      category: PROVIDER_DOCUMENTS_PREFIX,
    });

    const response = await https().post(`/skypointProvider/${providerId}/documents`, {
      name: file.name,
      fileUrl: uploadURL,
      fileType: file.type,
      fileSize: file.size,
      ...(folderId && { folder: folderId }),
      ...(expiry && { expiry }),
    });

    if (response?.status === 200) {
      return response?.data?.document;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message || error?.message, error?.response?.status);
    return false;
  } finally {
    onProgress?.(false);
  }
};

// Records a file that has already been pushed to S3 against the provider.
// The shared upload modal does its own S3 upload before calling this, so it
// hands over the resulting URL rather than a File.
const addProviderDocumentByUrl = async (providerId, { name, fileUrl, fileType, fileSize, folder, expiry }) => {
  try {
    const response = await https().post(`/skypointProvider/${providerId}/documents`, {
      name,
      fileUrl,
      fileType,
      fileSize,
      ...(folder && { folder }),
      ...(expiry && { expiry }),
    });
    return response?.status === 200 ? response?.data?.document || true : false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// Same as replaceProviderDocument but for a file already uploaded to S3.
const replaceProviderDocumentByUrl = async (
  providerId,
  documentId,
  { name, fileUrl, fileType, fileSize, expiry }
) => {
  try {
    const response = await https().put(
      `/skypointProvider/${providerId}/documents/${documentId}/replace`,
      { name, fileUrl, fileType, fileSize, ...(expiry && { expiry }) }
    );
    return response?.status === 200 ? response?.data?.document || true : false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// Uploads a replacement for an existing document. The row keeps its id, folder
// and place in the list; only the underlying file and expiry change, and the
// document stops being flagged expired.
const replaceProviderDocument = async (providerId, documentId, file, onProgress, expiry) => {
  const validationError = validateProviderDocument(file);
  if (validationError) {
    showError(validationError);
    return false;
  }

  try {
    onProgress?.(true);
    const { uploadURL } = await uploadFileToS3Api({
      file,
      category: PROVIDER_DOCUMENTS_PREFIX,
    });

    const response = await https().put(
      `/skypointProvider/${providerId}/documents/${documentId}/replace`,
      {
        name: file.name,
        fileUrl: uploadURL,
        fileType: file.type,
        fileSize: file.size,
        ...(expiry && { expiry }),
      }
    );

    if (response?.status === 200) {
      swal({ title: "Document replaced", icon: "success", dangerMode: false });
      return response?.data?.document || true;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message || error?.message, error?.response?.status);
    return false;
  } finally {
    onProgress?.(false);
  }
};

// Sets an expiry reminder, or clears it by passing null — the document stays
// where it is and simply stops being chased in Pending Actions.
const setProviderDocumentExpiry = async (providerId, documentId, expiry) => {
  try {
    const response = await https().put(
      `/skypointProvider/${providerId}/documents/${documentId}/expiry`,
      { expiry: expiry || null }
    );
    if (response?.status === 200) {
      swal({
        title: expiry ? "Expiry date updated" : "Reminder cleared",
        icon: "success",
        dangerMode: false,
      });
      return true;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// Renames the display name only. The backend keeps the original extension, so
// a name typed without one still comes back as "report.pdf".
const renameProviderDocument = async (providerId, documentId, name) => {
  try {
    const response = await https().put(`/skypointProvider/${providerId}/documents/${documentId}`, { name });
    if (response?.status === 200) {
      return response?.data?.document || true;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const deleteProviderDocument = async (providerId, documentId) => {
  try {
    const response = await https().delete(`/skypointProvider/${providerId}/documents/${documentId}`);
    if (response?.status === 200) {
      swal({ title: "Document deleted", icon: "success", dangerMode: false });
      return true;
    }
    return false;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

export {
  getProviderDocuments,
  uploadProviderDocument,
  addProviderDocumentByUrl,
  replaceProviderDocument,
  replaceProviderDocumentByUrl,
  setProviderDocumentExpiry,
  renameProviderDocument,
  deleteProviderDocument,
  getProviderFolders,
  createProviderFolder,
  renameProviderFolder,
  deleteProviderFolder,
};
